import apiClient, { api } from './apiClient';

/**
 * Vendor Onboarding — typed API client.
 *
 * Mirrors erp-api/src/{routes,controllers,services,models}/vendorOnboarding.js
 * and database/migrations/018_scm_vendor_onboarding.sql exactly. Row shapes
 * below are the raw SQL columns returned by the API — nothing here is
 * invented or renamed, so every field that can be `null` in the database is
 * typed as `| null`, never defaulted to 0/''/false in this layer.
 *
 * Unwrap rules (see services/apiClient.ts):
 *  - GET / POST responses are auto-unwrapped to `response.data.data` by `api`.
 *  - PUT / PATCH are NOT auto-unwrapped — callers here read `.data.data` by hand.
 *  - The multipart document upload goes through `api.post` too (still a POST),
 *    but must strip the instance's default JSON Content-Type so the browser
 *    can set the multipart boundary itself.
 */

// ---------------------------------------------------------------------------
// Enums (from the migration's ENUM columns / validators/vendorOnboarding.js)
// ---------------------------------------------------------------------------

export type VendorOnboardingStatus =
  | 'DRAFT'
  | 'INVITED'
  | 'SUBMITTED'
  | 'UNDER_VERIFICATION'
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'REJECTED';

export type VendorType = 'INDIVIDUAL' | 'COMPANY' | 'HUF' | 'PARTNERSHIP' | 'LLP' | 'TRUST';

export type MsmeCategory = 'MICRO' | 'SMALL' | 'MEDIUM' | 'NOT_REGISTERED';

export type VerificationKind = 'GSTIN' | 'PAN' | 'UDYAM' | 'BANK_ACCOUNT' | 'IFSC';

export type VerificationRequestKind = VerificationKind | 'ALL';

export type VerificationStatus = 'PENDING' | 'VERIFIED' | 'FAILED' | 'UNAVAILABLE';

export type DocumentType =
  | 'PAN_CARD'
  | 'GST_CERTIFICATE'
  | 'UDYAM_CERTIFICATE'
  | 'CANCELLED_CHEQUE'
  | 'BANK_STATEMENT'
  | 'INCORPORATION_CERT'
  | 'ADDRESS_PROOF'
  | 'ISO_CERT'
  | 'OTHER';

export type DocumentStatus = 'PENDING' | 'VERIFIED' | 'REJECTED' | 'EXPIRED';
export type DocumentReviewStatus = 'VERIFIED' | 'REJECTED';

export const VENDOR_TYPES: VendorType[] = ['INDIVIDUAL', 'COMPANY', 'HUF', 'PARTNERSHIP', 'LLP', 'TRUST'];
export const MSME_CATEGORIES: MsmeCategory[] = ['MICRO', 'SMALL', 'MEDIUM', 'NOT_REGISTERED'];
export const VERIFICATION_KINDS: VerificationKind[] = ['GSTIN', 'PAN', 'UDYAM', 'BANK_ACCOUNT', 'IFSC'];
export const DOCUMENT_TYPES: DocumentType[] = [
  'PAN_CARD',
  'GST_CERTIFICATE',
  'UDYAM_CERTIFICATE',
  'CANCELLED_CHEQUE',
  'BANK_STATEMENT',
  'INCORPORATION_CERT',
  'ADDRESS_PROOF',
  'ISO_CERT',
  'OTHER',
];

// ---------------------------------------------------------------------------
// Row shapes
// ---------------------------------------------------------------------------

export interface VendorOnboardingApplication {
  id: number;
  tenant_id: number;
  vendor_id: number | null;
  reference_no: string;
  legal_name: string;
  trade_name: string | null;
  contact_person: string | null;
  email: string;
  phone: string | null;
  vendor_type: VendorType;
  gstin: string | null;
  pan: string | null;
  udyam_number: string | null;
  msme_category: MsmeCategory | null;
  msme_verified_at: string | null;
  status: VendorOnboardingStatus;
  rejection_reason: string | null;
  invite_sent_at: string | null;
  invite_expires_at: string | null;
  submitted_at: string | null;
  approved_by: number | null;
  approved_at: string | null;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export interface VendorVerification {
  id: number;
  tenant_id: number;
  onboarding_id: number | null;
  vendor_id: number | null;
  kind: VerificationKind;
  value: string;
  status: VerificationStatus;
  /** Which implementation answered ('mock' today). A value checked offline
   * must never be presented as one confirmed against a government registry. */
  provider: string;
  legal_name_returned: string | null;
  raw_response: Record<string, unknown> | null;
  failure_reason: string | null;
  verified_at: string | null;
  verified_by: number | null;
  created_at: string;
}

export interface VendorDocument {
  id: number;
  tenant_id: number;
  onboarding_id: number | null;
  vendor_id: number | null;
  document_type: DocumentType;
  file_name: string;
  stored_path: string;
  mime_type: string | null;
  size_bytes: number;
  valid_until: string | null;
  status: DocumentStatus;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_notes: string | null;
  uploaded_by: number | null;
  created_at: string;
}

export interface VendorContact {
  id: number;
  tenant_id: number;
  vendor_id: number;
  name: string;
  designation: string | null;
  email: string | null;
  phone: string | null;
  contact_type: 'PRIMARY' | 'ACCOUNTS' | 'LOGISTICS' | 'ESCALATION' | 'OTHER';
  is_active: 0 | 1;
  created_at: string;
}

export interface VendorAddress {
  id: number;
  tenant_id: number;
  vendor_id: number;
  address_type: 'REGISTERED' | 'BILLING' | 'SHIPPING' | 'REMIT_TO';
  line1: string;
  line2: string | null;
  city: string | null;
  state: string | null;
  state_code: string | null;
  pincode: string | null;
  country: string;
  is_default: 0 | 1;
  created_at: string;
}

export interface VendorBankAccount {
  id: number;
  tenant_id: number;
  vendor_id: number;
  account_name: string;
  account_number: string;
  ifsc_code: string;
  bank_name: string | null;
  branch: string | null;
  is_primary: 0 | 1;
  verified_at: string | null;
  verification_id: number | null;
  created_at: string;
}

export interface VendorOnboardingFull extends VendorOnboardingApplication {
  verifications: VendorVerification[];
  documents: VendorDocument[];
  contacts: VendorContact[];
  addresses: VendorAddress[];
  bankAccounts: VendorBankAccount[];
}

// ---------------------------------------------------------------------------
// Request / response payloads
// ---------------------------------------------------------------------------

export interface ApplicantFields {
  legalName?: string;
  tradeName?: string | null;
  contactPerson?: string | null;
  email?: string;
  phone?: string | null;
  vendorType?: VendorType;
  gstin?: string | null;
  pan?: string | null;
  udyamNumber?: string | null;
  msmeCategory?: MsmeCategory | null;
}

export interface ListFilters {
  status?: VendorOnboardingStatus;
  search?: string;
  page?: number;
  limit?: number;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface ListResult {
  applications: VendorOnboardingApplication[];
  pagination: Pagination;
}

export interface SummaryData {
  total: number;
  byStatus: Partial<Record<VendorOnboardingStatus, number>>;
  pendingVerification: number;
  documentsExpiringSoon: number;
}

export interface InviteResult {
  application: VendorOnboardingApplication;
  /** Returned once, at invite time only — the raw token is never
   * retrievable again (only its hash is persisted). SMTP may be
   * unconfigured, so this URL is the only reliable delivery path. */
  inviteUrl: string;
}

export interface ResolvedInvite {
  onboardingId: number;
  referenceNo: string;
  legalName: string;
  email: string;
  expiresAt: string;
}

export interface VerifyRequest {
  kind?: VerificationRequestKind;
  value?: string;
  values?: Partial<Record<VerificationKind, string>>;
}

export interface VerifyResult {
  application: VendorOnboardingApplication;
  verifications: VendorVerification[];
}

export interface ApproveRequest {
  contacts?: Array<{ name: string; designation?: string; email?: string; phone?: string; contactType?: string }>;
  addresses?: Array<{
    addressType?: string;
    line1: string;
    line2?: string;
    city?: string;
    state?: string;
    stateCode?: string;
    pincode?: string;
    country?: string;
    isDefault?: boolean;
  }>;
  bankAccounts?: Array<{
    accountName?: string;
    accountNumber: string;
    ifscCode: string;
    bankName?: string;
    branch?: string;
    isPrimary?: boolean;
  }>;
}

export interface ApproveResult {
  onboarding: VendorOnboardingApplication;
  vendorId: number;
}

export interface SubmitForApprovalResult {
  autoApproved: boolean;
  onboarding: VendorOnboardingApplication;
  vendorId?: number;
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

const BASE = '/vendor-onboarding';

export const vendorOnboardingService = {
  list: (filters: ListFilters = {}): Promise<ListResult> =>
    api.get<ListResult>(BASE, { params: filters }),

  summary: (): Promise<SummaryData> => api.get<SummaryData>(`${BASE}/summary`),

  getFull: (id: number | string): Promise<VendorOnboardingFull> =>
    api.get<{ application: VendorOnboardingFull }>(`${BASE}/${id}`).then((r) => r.application),

  create: (data: ApplicantFields): Promise<VendorOnboardingApplication> =>
    api
      .post<{ application: VendorOnboardingApplication }>(BASE, data)
      .then((r) => r.application),

  // PUT is not auto-unwrapped by apiClient — read the envelope by hand.
  update: (id: number | string, data: ApplicantFields): Promise<VendorOnboardingApplication> =>
    apiClient
      .put<{ status: string; message: string; data: { application: VendorOnboardingApplication } }>(
        `${BASE}/${id}`,
        data
      )
      .then((r) => r.data.data.application),

  invite: (id: number | string, expiresInDays?: number): Promise<InviteResult> =>
    api.post<InviteResult>(`${BASE}/${id}/invite`, expiresInDays ? { expiresInDays } : {}),

  // Public — no auth header is required by the backend for this one, but the
  // shared apiClient instance is reused for consistency; it simply attaches
  // no Authorization header when the caller has no session.
  resolveInvite: (token: string): Promise<ResolvedInvite> =>
    api.get<ResolvedInvite>(`${BASE}/invite/${encodeURIComponent(token)}`),

  submit: (id: number | string): Promise<VendorOnboardingApplication> =>
    api
      .post<{ application: VendorOnboardingApplication }>(`${BASE}/${id}/submit`, {})
      .then((r) => r.application),

  verify: (id: number | string, body: VerifyRequest = {}): Promise<VerifyResult> =>
    api.post<VerifyResult>(`${BASE}/${id}/verify`, body),

  listDocuments: (id: number | string): Promise<VendorDocument[]> =>
    api
      .get<{ documents: VendorDocument[]; count: number }>(`${BASE}/${id}/documents`)
      .then((r) => r.documents),

  uploadDocument: (
    id: number | string,
    file: File,
    documentType: DocumentType,
    validUntil?: string | null
  ): Promise<VendorDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('documentType', documentType);
    if (validUntil) formData.append('validUntil', validUntil);

    return api.post<{ document: VendorDocument }>(`${BASE}/${id}/documents`, formData, {
      // Strip the instance's default 'application/json' so the browser sets
      // 'multipart/form-data; boundary=...' itself.
      headers: { 'Content-Type': undefined } as any,
    }).then((r) => r.document);
  },

  // PATCH is not auto-unwrapped either.
  reviewDocument: (
    docId: number | string,
    status: DocumentReviewStatus,
    notes?: string
  ): Promise<VendorDocument> =>
    apiClient
      .patch<{ status: string; message: string; data: { document: VendorDocument } }>(
        `${BASE}/documents/${docId}/review`,
        { status, notes }
      )
      .then((r) => r.data.data.document),

  submitForApproval: (id: number | string): Promise<SubmitForApprovalResult> =>
    api.post<SubmitForApprovalResult>(`${BASE}/${id}/submit-for-approval`, {}),

  approve: (id: number | string, body: ApproveRequest = {}): Promise<ApproveResult> =>
    api.post<ApproveResult>(`${BASE}/${id}/approve`, body),

  reject: (id: number | string, reason: string): Promise<VendorOnboardingApplication> =>
    api
      .post<{ application: VendorOnboardingApplication }>(`${BASE}/${id}/reject`, { reason })
      .then((r) => r.application),
};

export default vendorOnboardingService;
